import React, { PureComponent, Fragment } from 'react'
import i18n from 'i18next'
import { reaction } from 'mobx'
import { observer } from 'mobx-react'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import ChartSelector from '../ChartSelector'
import colours from '../Chart/colours'
import Chart from '../Chart/index'
import ReportPageTitle from '../ReportPageTitle'
import { getQuestionTitle } from '../../../../../utils/QuestionHelper'
import Panel from '../../../../../UniformComponents/Panel'
import PanelHeader from '../../../../../UniformComponents/PanelHeader'
import QuestionFooter from '../QuestionFooter'
import DebugInfo from '../DebugInfo'
import QuestionEdit from '../QuestionEdit'

@observer
class NumberRange extends PureComponent {
    constructor(props) {
        super(props)
        const {
            reportStore,
            question,
            dataKey,
            store,
        } = props

        const { survey } = store
        this.isMaxbiReport = survey.isMaxbi()
        this.showChart = this.isMaxbiReport ? reportStore.showChart(question) : true
        this.data = reportStore.reportDataV2.questions[props.dataKey]
        this.categories = R.pathOr([], ['yOptions'])(this.data).map((option) => option.label)
        this.state = {
            updated: new Date().getTime(),
            sorting: {
                direction: undefined,
                sortBy: undefined,
                defaultSorting: Array.from(Array(this.categories.length).keys()),
                sortedIndexArray: Array.from(Array(this.categories.length).keys()),
            },
            chartType: R.pathOr('column', ['settings', 'report', 'graphType'], question),
        }
        this.chartKey = uuidv4()
        this.chartColours = [
            colours.getSerieColour('survey'),
            colours.getSerieColour('comparison'),
        ]

        reaction(
            () => reportStore.reportDataFetched,
            () => {
                this.categories = reportStore
                    .reportData
                    .questions[dataKey]
                    .yOptions
                    .map((option) => option.label)
                this.data = reportStore.reportDataV2.questions[props.dataKey]
                this.chartKey = uuidv4()
                this.setState({
                    updated: props.reportStore.reportDataFetched,
                    sorting: {
                        direction: undefined,
                        sortBy: undefined,
                        defaultSorting: Array.from(Array(this.categories.length).keys()),
                        sortedIndexArray: Array.from(Array(this.categories.length).keys()),
                    },
                })
            },
        )

        this.chartType = question.settings.report.graphType
        reportStore.questionSorting[dataKey] = this.getQuestionSorting
    }

    componentWillUnmount() {
        const { reportStore, dataKey } = this.props
        Reflect.deleteProperty(reportStore.questionSorting, dataKey)
    }

    getQuestionSorting = () => {
        const { sorting } = this.state
        return [sorting.sortedIndexArray, this.chartType]
    }

    getResponseValue = (option, index, data) => {
        const { reportStore } = this.props
        const { areaUnit } = reportStore

        let value = option //
        if (data.isAreaQuestion) {
            const postFix = areaUnit === 'M2'
                ? 'M<sup>2</sup>'
                : 'Ft<sup>2</sup>'
            const convertedValue = areaUnit === 'M2'
                ? value
                : value * 10.764
            value = `${convertedValue.toFixed(2)} ${postFix}`
        }
        return value
    }

    generateDataTable = () => {
        const { sorting } = this.state
        const { reportStore, dataKey } = this.props
        const data = reportStore.reportDataV2.questions[dataKey]

        const cols = this.isMaxbiReport ? [
            {
                label: 'Number',
                class: 'tleft',
                type: 'areaTitle',
            },
            {
                label: i18n.t('report_question_selected'),
                class: 'tright',
                path: 'numberOfResponses',
                type: 'optionSelected',
            },
        ] : [
            {
                label: 'Number',
                class: 'tleft',
                type: 'optionTitle',
            },
            {
                label: i18n.t('report_question_responses'),
                path: 'numberOfResponses',
                type: 'val',
            },
            {
                label: reportStore.surveyTitle(),
                path: 'pctOfResponses',
                type: 'pct',
            },
            // ...averages,
        ]

        this.cols = cols.slice(2)

        return (
            <div style={{ overflowX: 'auto' }}>
                <table
                    className="datatable question tablesorter tablesorter-default"
                    style={{
                        width: '100%',
                    }}
                >
                    <thead>
                        <tr className="col tablesorter-headerRow">
                            {cols
                                .map((col) => (
                                    this.isMaxbiReport
                                        ? (
                                            <th className={`${col.class || ''} tablesorter-header`}>
                                                <div className="tablesorter-header-inner">
                                                    {col.label}
                                                </div>
                                            </th>
                                        ) : (
                                            <th style={{ cursor: 'pointer' }} className={`${col.class || ''} tablesorter-header`} onClick={() => this.sortBy(col)}>
                                                <div className="tablesorter-header-inner">
                                                    {col.label}
                                                    {this.sortIcon(col)}
                                                </div>
                                            </th>
                                        )
                                ))}
                            <td className="tlast" />
                        </tr>
                    </thead>
                    <tbody>
                        {sorting.sortedIndexArray.map((index) => {
                            const option = this.categories[index]
                            return (
                                <tr className="col" key={`option_${option.id}`}>
                                    {cols
                                        .map((col) => (
                                            this.colMapper(col, data, option, index)
                                        ))}
                                    <td className="tlast" />
                                </tr>
                            )
                        })}
                    </tbody>
                    {!this.isMaxbiReport && (
                        <tfoot>
                            <tr className="col">
                                <th>
                                    {i18n.t('report_average')}
                                    :
                                </th>
                                <td />
                                <td>
                                    {R.pathOr(0, ['aggregate', 'average'])(data).toFixed(2)}
                                </td>
                                <td className="tlast" />
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        )
    }

    colMapper = ({ path, type }, data, option, index) => {
        let col = null
        if (type === 'pct') {
            col = (
                <td>
                    {`${R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)} %`}
                </td>
            )
        } else if (type === 'areaTitle') {
            col = (
                <th
                    dangerouslySetInnerHTML={{ __html: this.getResponseValue(option, index, data) }}
                />
            )
        } else if (type === 'optionTitle') {
            col = (
                <th>
                    {option}
                </th>
            )
        } else if (type === 'val') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data)}
                </td>
            )
        } else if (type === 'avg') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}
                </td>
            )
        } else if (type === 'optionSelected') {
            col = (
                <td style={{ textAlign: 'center' }}>
                    <i className="fas fa-check" />
                </td>
            )
        }
        return col
    }

    sortBy = (col) => {
        const sorting = R.clone(this.state.sorting)
        const { reportStore, dataKey } = this.props
        const data = reportStore.reportDataV2.questions[dataKey]
        console.log(`sortBy ${JSON.stringify(col)}`)

        const oldSortBy = sorting.sortBy

        let values = []
        if (!col.path) {
            sorting.sortBy = 'label'
            values = this.categories
        } else {
            sorting.sortBy = col.path
            values = R.pathOr(0, col.path.split('.'))(data)
        }
        const sortedIndexArray = Array
            .from(Array(values.length).keys())
            .sort((a, b) => (values[a] < values[b] ? -1 : values[a] > values[b] ? 1 : 0))

        if (sorting.sortBy !== oldSortBy || !sorting.direction) {
            sorting.direction = 'ASC'
        } else {
            sorting.direction = sorting.direction === 'ASC'
                ? 'DESC'
                : undefined
        }

        if (sorting.direction === 'ASC') {
            sorting.sortedIndexArray = sortedIndexArray
        } else if (sorting.direction === 'DESC') {
            sorting.sortedIndexArray = sortedIndexArray.reverse()
        } else {
            sorting.sortedIndexArray = sorting.defaultSorting
        }
        this.setState({
            sorting,
        })
    }

    sortIcon = ({ path }) => {
        const { sorting } = this.state
        const { direction, sortBy } = sorting
        let icon = <span style={{ display: 'inline' }} />
        if (
            direction
            && (
                (!path && sortBy === 'label')
                || path === sortBy
            )
        ) {
            const iconClass = direction === 'ASC'
                ? 'fal fa-long-arrow-up'
                : 'fal fa-long-arrow-down'
            icon = <i style={{ marginLeft: '5px', width: '6px', color: '#81859B' }} className={`${iconClass}`} />
        }

        return icon
    }

    getChartCategorySet = () => {
        const { sorting } = this.state
        const { sortedIndexArray } = sorting
        console.log('sorted categoryMap', sortedIndexArray.map((sortedIndex) => this.categories[sortedIndex]))
        return sortedIndexArray.map((sortedIndex) => this.categories[sortedIndex])
    }

    getChartCategories = () => {
        const { categories } = this
        const { sorting } = this.state
        const { sortedIndexArray } = sorting
        console.log('sorted categories', sortedIndexArray.map((sortedIndex) => categories[sortedIndex]))
        return sortedIndexArray.map((sortedIndex) => categories[sortedIndex])
    }

    render() {
        const {
            question,
            store,
            reportStore,
            reportFilter,
            dataKey,
            pageId,
        } = this.props
        const { survey } = store
        const { chartType } = this.state
        const data = R.or(reportStore.reportDataV2.questions[dataKey], {})
        return (
            <Panel cssClass="question">
                <PanelHeader>
                    <div id={`questionTitle_${question.pageQuestionId}`} className="pageTitleContainer">
                        <ReportPageTitle
                            h2={getQuestionTitle({
                                content: question.content.shortTitle,
                                formContent: R.pathOr(null, ['formContent', 'shortTitle'])(question),
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            h3={getQuestionTitle({
                                content: question.content.longTtile,
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            reportStore={reportStore}
                            questionTypeCode={question.questionTypeCode}
                        />
                        <QuestionEdit reportStore={reportStore} pageId={pageId} />
                    </div>
                </PanelHeader>
                <DebugInfo
                    store={store}
                    questionTypeCode={question.questionTypeCode}
                    reportStore={reportStore}
                    questionId={question.id}
                    pageQuestionId={question.pageQuestionId}
                />
                <div id={`questionData_${question.pageQuestionId}`}>
                    {this.generateDataTable()}
                </div>
                {this.showChart && this.categories.length > 0 && (
                    <Fragment>
                        <ChartSelector
                            chartType={chartType}
                            onChange={(chartType) => { this.setState({ chartType }) }}
                        />
                        <Chart
                            colors={this.chartColours}
                            key={this.chartKey}
                            survey={survey}
                            data={data}
                            dataKey={dataKey}
                            pointEvent={this.chartFilter}
                            question={question}
                            reportFilter={reportFilter}
                            series={this.cols}
                            sortedIndexArray={this.state.sorting.sortedIndexArray}
                            pageQuestionId={question.pageQuestionId}
                            id={question.pageQuestionId}
                            categorySet={this.getChartCategorySet()}
                            chartType={chartType}
                            questionTypeCode={question.questionTypeCode}
                            kpiType={question.metadata.kpiType}
                            categories={this.getChartCategories()}
                            reportStore={reportStore}
                        />
                        <QuestionFooter
                            dataKey={dataKey}
                            reportStore={reportStore}
                        />
                    </Fragment>
                )}
            </Panel>
        )
    }
}

export default NumberRange
